import * as React from 'react';
import { DateRange, getDefaultDateRange, isPredefinedRange, PredefinedDateRange } from 'src/utils/date';
import { getPredefinedDates } from 'src/components/DatePicker';
import qs from 'qs';
import { GetListLimit } from 'src/dto/Limits';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDateRangeFilter, setDateRangeFilter } from 'src/frontend-common/localstorage';
import { GetCustomersSort } from 'src/frontend-common/dto/Types/CustomerSortFieldType';

const getUpdatedDateRange = (d: { startDate: Date; endDate: Date } | PredefinedDateRange | null): { startDate: Date; endDate: Date } | PredefinedDateRange => {
  if (!d) {
    return getDefaultDateRange();
  }
  if (!isPredefinedRange(d)) {
    return d;
  }

  const newFilter = getPredefinedDates().find((pd) => pd.label === d.label);
  if (newFilter === undefined) {
    return d;
  }
  return { label: newFilter.label, ...newFilter.range() };
};

type ValueType = 'sort' | 'page' | 'limit' | 'dateRange' | 'token' | 'tab';

interface QueryType {
  sort: GetCustomersSort;
  page: number;
  startDate: Date;
  endDate: Date;
  limit: GetListLimit;
  token: string;
  tab: number;
}

function useQuery() {
  const query = useLocation().search;
  return qs.parse(query, { ignoreQueryPrefix: true }) as unknown as QueryType;
}

const QueryValuesLayout: React.FC<{
  excludeValues?: Array<ValueType>;
  children: (i: {
    sort: GetCustomersSort;
    setSort: (s: GetCustomersSort) => void;
    page: number;
    setPage: (s: number) => void;
    dateRange: DateRange;
    setDateRange: (d: DateRange) => void;
    limit: GetListLimit;
    setLimit: (d: GetListLimit) => void;
    token: string;
    setToken: (s: string) => void;
    tab: number;
    setTab: (s: number) => void;
  }) => React.ReactNode;
}> = ({ excludeValues, children }) => {
  const query = useQuery();
  const [dateRangeFilter, setDateRangeComponentFilter] = React.useState<{ startDate: Date; endDate: Date; label?: string } | null>(
    getDateRangeFilter() || null
  );

  const [sort, setSort] = React.useState(query.sort || 'dateDesc');

  const [limit, setLimit] = React.useState(query.limit || 10);

  const [page, setPage] = React.useState(query.page || 0);

  const [tab, setTab] = React.useState(query.tab || 0);

  const [token, setToken] = React.useState(query.token || '');

  const getValuesQuery = ({
    dateRange,
    sort,
    page,
    limit,
    token,
    tab,
  }: {
    dateRange: DateRange;
    sort: GetCustomersSort;
    page: number;
    tab: number;
    limit: GetListLimit;
    token: string;
  }) => {
    if (!excludeValues) {
      return qs.stringify({
        dateRange: dateRange,
        sort: sort,
        page: page,
        tab: tab,
        limit: limit,
        token: token,
      });
    } else {
      const dateRangeObj = excludeValues.indexOf('dateRange') === -1 && dateRange ? dateRange : {};
      return qs.stringify({
        ...dateRangeObj,
        sort: excludeValues.indexOf('sort') === -1 ? sort : undefined,
        page: excludeValues.indexOf('page') === -1 ? page : undefined,
        limit: excludeValues.indexOf('limit') === -1 ? limit : undefined,
        token: excludeValues.indexOf('token') === -1 ? token : undefined,
        tab: excludeValues.indexOf('tab') === -1 ? tab : undefined,
      });
    }
  };

  // needed to update the end_date to now
  const dateRange = React.useMemo(() => {
    return getUpdatedDateRange(dateRangeFilter);
  }, [dateRangeFilter]);
  const location = useLocation();
  const navigate = useNavigate();
  const replaceQueryInURL = () => {
    const newQuery = getValuesQuery({
      dateRange,
      sort,
      page,
      limit,
      token,
      tab,
    });

    navigate(`${location.pathname}?${newQuery}`, {
      replace: true,
      state: location.state,
    });
  };

  React.useEffect(() => {
    if (query.startDate && query.endDate) {
      const updatedFilter = getUpdatedDateRange({ startDate: query.startDate, endDate: query.endDate });
      setDateRangeFilter(updatedFilter);
      return;
    }
    replaceQueryInURL();
  }, []);

  React.useEffect(() => {
    replaceQueryInURL();
  }, [dateRange, sort, page, limit, tab]);

  return (
    <>
      {children({
        sort,
        dateRange,
        setSort,
        page,
        setPage,
        limit,
        setLimit,
        setDateRange: (dateRange: { startDate: Date; endDate: Date; label?: string }) => {
          setDateRangeComponentFilter(dateRange);
          setDateRangeFilter(dateRange);
        },
        token,
        setToken,
        tab,
        setTab,
      })}
    </>
  );
};

export default QueryValuesLayout;
